import {
    Flex,
    Text,
    Input,
    Box,
    Spinner,
} from '@chakra-ui/react';
import { lazy, Suspense, useEffect, useReducer } from 'react';
import { useCashFlowProjectionsData } from '../hooks/useCashFlowProjectionsData';
import FreeCashFlowHistoryTable from './cashFlowProjections/FreeCashFlowHistoryTable';
import ProjectedCashFlowsTable from './cashFlowProjections/ProjectedCashFlowsTable';
import RateSliderControl from './cashFlowProjections/RateSliderControl';
import ValuationSummary from './cashFlowProjections/ValuationSummary';
import { initialProjectionState, projectionReducer } from './cashFlowProjections/projectionReducer';
import { useProjectedCashFlows } from './cashFlowProjections/useProjectedCashFlows';

const CashFlowProjectionChart = lazy(() => import('./cashFlowProjections/CashFlowProjectionChart'));

const GROWTH_MARKERS = [
    { value: -50, label: '-50%' },
    { value: 0, label: '0%' },
    { value: 100, label: '100%' },
];

const DISCOUNT_MARKERS = [
    { value: -10, label: '-10%' },
    { value: 0, label: '0%' },
    { value: 10, label: '10%' },
    { value: 20, label: '20%' },
    { value: 30, label: '30%' },
    { value: 40, label: '40%' },
    { value: 50, label: '50%+' },
];

// currently only used in the tutorial, not in the stock analysis page (May 27, 2025)
function CashFlowProjectionsComponent(props) {
    const tickerSymbol = props.ticker;
    const [{ growthRate, discountRate, userInitialFCF }, dispatchProjection] = useReducer(
        projectionReducer,
        initialProjectionState
    );

    const {
        loading,
        error,
        operatingCashFlows,
        initialFreeCashFlow,
        outstandingShares,
    } = useCashFlowProjectionsData(tickerSymbol);

    useEffect(() => {
        dispatchProjection({ type: 'reset_user_initial_fcf' });
    }, [tickerSymbol, initialFreeCashFlow]);

    const initialFCF = userInitialFCF ?? initialFreeCashFlow ?? 0;
    const { cashFlows, totalValue } = useProjectedCashFlows(initialFCF, growthRate, discountRate);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', paddingLeft: '10px', paddingRight: '10px' }}>
            <Flex
                justifyContent="space-around"
                align="flex-start"
                gap={{ base: 8, lg: 5 }}
                direction={{ base: 'column', lg: 'row' }}
                width="100%"
            >
                <Flex
                    flexDirection="column"
                    align={{ base: 'stretch', lg: 'center' }}
                    width={{ base: '100%', lg: '45%' }}
                    minW={0}
                >
                    <FreeCashFlowHistoryTable
                        tickerSymbol={tickerSymbol}
                        loading={loading}
                        operatingCashFlows={operatingCashFlows}
                    />

                    {error && <div>Error: {error}</div>}

                    <Flex width='100%' justifyContent={'center'} alignItems={'center'} gap={4} mt={4} mb={4}>
                        <Text fontSize="lg" fontWeight="bold">Free Cash Flow:</Text>
                        <Input
                            value={initialFCF}
                            onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val) && val >= 0) {
                                    dispatchProjection({ type: 'set_user_initial_fcf', payload: val });
                                }
                            }}
                            width="200px"
                            textAlign="center"
                        />
                    </Flex>

                    <ProjectedCashFlowsTable cashFlows={cashFlows} />
                </Flex>

                <Flex
                    flexDirection="column"
                    justifyContent="flex-start"
                    width={{ base: '100%', lg: '50%' }}
                    minW={0}
                >
                    <ValuationSummary totalValue={totalValue} outstandingShares={outstandingShares} />

                    <RateSliderControl
                        label="FCF Growth Rate:"
                        value={growthRate}
                        onChange={(val) => dispatchProjection({ type: 'set_growth', payload: val })}
                        min={-50}
                        max={100}
                        markers={GROWTH_MARKERS}
                        defaultValue={0}
                        trackBg="green.100"
                        rangeBg="green"
                    />

                    <RateSliderControl
                        label="Discount Rate:"
                        value={discountRate}
                        onChange={(val) => dispatchProjection({ type: 'set_discount', payload: val })}
                        min={-10}
                        max={50}
                        markers={DISCOUNT_MARKERS}
                        trackBg="red.100"
                        rangeBg="tomato"
                        thumbSize={6}
                    />

                    <Suspense
                        fallback={(
                            <Box width="100%" height="400px" display="flex" alignItems="center" justifyContent="center">
                                <Spinner size="lg" color="green.500" />
                            </Box>
                        )}
                    >
                        <CashFlowProjectionChart cashFlows={cashFlows} />
                    </Suspense>
                </Flex>
            </Flex>
        </div>
    );
}

export default CashFlowProjectionsComponent;
